import React, { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Package as PackageIcon, Trash2, Edit3 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", category: "Wellness", description: "", test_ids: [], price: "", discounted_price: "", fasting_required: false, sample_type: "Blood", turnaround_hours: 24, is_featured: false };

export default function LabPackages() {
  const [pkgs, setPkgs] = useState([]);
  const [tests, setTests] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [p, t] = await Promise.all([api.get("/packages"), api.get("/tests")]);
    setPkgs(p.data || []); setTests(t.data || []);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...form, price: Number(form.price), discounted_price: form.discounted_price ? Number(form.discounted_price) : null, turnaround_hours: Number(form.turnaround_hours) };
    try {
      if (editing) await api.put(`/packages/${editing.id}`, body);
      else await api.post("/packages", body);
      toast.success(editing ? "Package updated" : "Package created");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (err) { toast.error(apiError(err, "Save failed")); }
  };

  const startEdit = (p) => { setEditing(p); setForm({ ...p, price: String(p.price), discounted_price: p.discounted_price ? String(p.discounted_price) : "" }); setOpen(true); };
  const remove = async (id) => {
    if (!window.confirm("Deactivate this package?")) return;
    try { await api.delete(`/packages/${id}`); toast.success("Package removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const testsById = useMemo(() => Object.fromEntries(tests.map((t) => [t.id, t])), [tests]);

  return (
    <DashboardLayout role="lab_admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><PackageIcon className="w-6 h-6 text-efgh-orange" /> Packages</h1>
          <p className="text-ink/60">Bundle multiple tests at a package price. Featured packages appear on the EFGH marketplace.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="new-package" className="bg-ink hover:bg-ink/90 text-white"><Plus className="w-4 h-4 mr-2" />New package</Button>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-3">
        {pkgs.length === 0 && <Card className="p-10 rounded-2xl text-center col-span-full text-ink/50">No packages yet — create your first bundle!</Card>}
        {pkgs.map((p) => (
          <Card key={p.id} className="p-5 rounded-2xl" data-testid={`pkg-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {p.is_featured && <span className="pill bg-efgh-yellow/20 text-efgh-yellow text-[10px] uppercase tracking-widest">Featured</span>}
                  <span className="text-xs text-ink/50">{p.category}</span>
                </div>
                <div className="mt-1 font-heading font-semibold text-ink text-lg">{p.name}</div>
                <p className="text-sm text-ink/60 mt-1 line-clamp-2">{p.description}</p>
                <div className="mt-2 text-xs text-ink/50">{(p.test_ids || []).length} tests · {p.parameters_count}+ parameters</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => startEdit(p)} className="p-2 text-ink/50 hover:text-ink" data-testid={`edit-pkg-${p.id}`}><Edit3 className="w-4 h-4" /></button>
                <button onClick={() => remove(p.id)} className="p-2 text-ink/50 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <div className="font-heading font-bold text-2xl text-ink">₹{p.discounted_price || p.price}</div>
              {p.discounted_price && <div className="text-sm text-ink/40 line-through">₹{p.price}</div>}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto" data-testid="pkg-dialog">
          <DialogTitle className="font-heading">{editing ? "Edit package" : "New package"}</DialogTitle>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Package name</Label><Input required data-testid="pkg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
              <div><Label>Sample type</Label><Input value={form.sample_type} onChange={(e) => setForm({ ...form, sample_type: e.target.value })} /></div>
              <div><Label>Price ₹</Label><Input required type="number" data-testid="pkg-price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div><Label>Discounted price ₹</Label><Input type="number" value={form.discounted_price} onChange={(e) => setForm({ ...form, discounted_price: e.target.value })} /></div>
              <div><Label>Turnaround (hrs)</Label><Input type="number" value={form.turnaround_hours} onChange={(e) => setForm({ ...form, turnaround_hours: e.target.value })} /></div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={form.fasting_required} onCheckedChange={(v) => setForm({ ...form, fasting_required: v })} />
                <span className="text-sm text-ink/70">Fasting required</span>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="pkg-featured" />
                <span className="text-sm text-ink/70">Feature this package on the EFGH marketplace</span>
              </div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>

            <div>
              <Label>Tests included</Label>
              <div className="mt-2 grid grid-cols-2 gap-1 max-h-56 overflow-auto p-2 border border-border rounded-lg">
                {tests.map((t) => {
                  const on = form.test_ids.includes(t.id);
                  return (
                    <label key={t.id} className={`flex items-center gap-2 p-2 rounded-md cursor-pointer text-sm ${on ? "bg-muted" : "hover:bg-muted/40"}`}>
                      <input type="checkbox" checked={on} onChange={(e) => setForm((f) => ({ ...f, test_ids: e.target.checked ? [...f.test_ids, t.id] : f.test_ids.filter((x) => x !== t.id) }))} />
                      <span className="flex-1 truncate">{t.name}</span>
                      <span className="text-xs text-ink/50">₹{t.discounted_price || t.price}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-ink/50 mt-1">{form.test_ids.length} tests selected</p>
            </div>

            <Button type="submit" data-testid="pkg-submit" className="w-full h-11 bg-ink hover:bg-ink/90 text-white">{editing ? "Update package" : "Create package"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
